import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

import isEmpty from "lodash-es/isEmpty";

import { Menu, MenuItem } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";

import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionTitle from "../../components/AccordionTitle";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import SearchBar from "../../components/faculty/SearchBar";

import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { doService } from "../../services/ServiceHelper";
import {
  getTeacherActivities,
  deleteActivity,
  changeOwner,
  cloneActivity,
  createActivityUserList,
} from "../../services/ActivityService";
import { getSchoolTeachers } from "../../services/SchoolService";

import SelectTeacherDialog from "../../components/faculty/SelectTeacherDialog";
import { showError, showToast, showPrompt } from "../../components/ATPDialogs";
import dayjs from "dayjs";
import { TableHead } from "@mui/material";
import { getSchoolStatus } from "../../services/SchoolService";
import { getUserPriority } from "../../services/UserService";

const ManageActivities = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);

  const [_activities, setActivities] = useState([]);
  const [_allTeachers, setAllTeachers] = useState([]);
  const [_dialogOpenOwner, setDialogOpenOwner] = useState(false);
  const [_dialogOpenClone, setDialogOpenClone] = useState(false);
  const [_selectedActivityEid, setSelectedActivityEid] = useState(null);

  const [_cloneName, setCloneName] = useState("");
  const [
    _cloneIncludeRestrictedEnrollment,
    setCloneIncludeRestrictedEnrollment,
  ] = useState(true);
  const [_cloneActivityDates, setCloneActivityDates] = useState(true);
  const [_schoolStatus, setSchoolStatus] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    initSchoolStatus();
    initTeacherActivities();
  }, []);

  const initSchoolStatus = () => {
    doService(getSchoolStatus(_school, _jwt), (results) => {
      setSchoolStatus(results.data);
    });
  };
  const initAllTeachers = () => {
    return doService(getSchoolTeachers(_school, _jwt), (results) => {
      const labeledLists = [];
      for (const teacher of results.data) {
        labeledLists.push({
          label: teacher.last_name + ", " + teacher.first_name,
          value: teacher.eid,
        });
      }
      setAllTeachers(labeledLists);
    });
  };

  const initTeacherActivities = () => {
    setShowProgress(true);
    doService(getTeacherActivities(_school, _jwt), (results) => {
      setActivities(results.data);
      setShowProgress(false);
    });
  };

  const handleClickCreate = () => {
    navigate(`/teacher/activity/create`);
  };

  const handleClickCalendar = () => {
    navigate(`/teacher/calendar`);
  };

  const handleClickEdit = (activityEid) => {
    navigate(`/teacher/activity/${activityEid}`);
  };

  const handleClickDelete = (activityEid) => {
    if (!window.confirm("Click OK to confirm delete, or Cancel.")) {
      return;
    }

    setShowProgress(true);
    doService(deleteActivity(_school, _jwt, activityEid), (results) => {
      initTeacherActivities();
      setShowProgress(false);
    });
  };

  const handleClickAvailableRooms = () => {
    navigate(`/report/available-rooms`);
  };

  const handleClickChangeOwner = (activityEid) => {
    setSelectedActivityEid(activityEid);
    if (_allTeachers.length === 0) {
      initAllTeachers().then(() => {
        // load proctors for activity...
        setDialogOpenOwner(true);
      });
    } else {
      setDialogOpenOwner(true);
    }
  };

  const handleCloseDialogOwner = () => {
    setDialogOpenOwner(false);
  };

  const handleOKOwner = (selected) => {
    if (selected == null) {
      showError("Unable to Change Owner", "Please select a teacher.");
      return;
    }
    setDialogOpenOwner(false);

    doService(
      changeOwner(_school, _jwt, _selectedActivityEid, selected.value),
      (results) => {
        showToast("S", "Activity Owner Changed");
        initTeacherActivities();
      },
    );
  };

  const handleCloseClone = () => setDialogOpenClone(false);

  const handleClickClone = (activity) => {
    setSelectedActivityEid(activity.eid);

    setCloneName("CLONE - " + activity.name);
    setCloneIncludeRestrictedEnrollment(true);
    setCloneActivityDates(true);
    setDialogOpenClone(true);
  };

  const handleChangeCloneName = (event) => setCloneName(event.target.value);

  const handleCloneOK = () => {
    if (isEmpty(_cloneName)) {
      showError("Unable to Clone", "Please specify an Activity Name.");
      return;
    }

    setDialogOpenClone(false);

    setShowProgress(true);
    doService(
      cloneActivity(
        _school,
        _jwt,
        _selectedActivityEid,
        _cloneName,
        _cloneIncludeRestrictedEnrollment,
        _cloneIncludeRestrictedEnrollment,
      ),
      (results) => {
        initTeacherActivities();
        setShowProgress(false);
        showToast("S", "Activity Cloned!");
      },
    );
  };

  const handleChangeCloneRestrictedEnrollment = (event) =>
    setCloneIncludeRestrictedEnrollment(event.target.checked);

  const handleChangeCloneActivityDates = (event) =>
    setCloneActivityDates(event.target.checked);

  const handleClickWhoCanView = (activityEid) => {
    navigate(`/teacher/activity/${activityEid}/who-can-view`);
  };

  const handleClickCreateStudentList = (activity) => {
    showPrompt(
      "title",
      "text",
      activity.name + " - " + dayjs().format("MM/DD/YY"),
      (value) => {
        doService(
          createActivityUserList(_school, _jwt, activity.eid, value),
          (results) => {
            initTeacherActivities();
            setShowProgress(false);
            showToast("S", "Student List Created!");
          },
        );
      },
    );
  };

  const handleClickPreviousAttendance = (activity) => {
    navigate("/report/previous-attendance");
  };

  const handleClickRqr = (activity) => {
    navigate(`/teacher/activity/attend/${activity.eid}`);
  };

  return (
    <>
      <DrawerAppBar />
      <SearchBar />

      <Container sx={{ mt: 1 }}>
        <Title text="My Activities" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        {_schoolStatus && _schoolStatus.teacherEditEnabled === "N" && (
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Your school has disabled the ability to delete activities.
          </Alert>
        )}

        <Stack direction="row" spacing={1} fullWidth>
          <Button key="signup" onClick={handleClickCreate} variant="contained">
            Create
          </Button>
          <Button
            key="calendar"
            onClick={handleClickCalendar}
            variant="contained"
            className="button1"
          >
            Calendar
          </Button>
          <Button
            key="delete"
            onClick={handleClickAvailableRooms}
            variant="outlined"
          >
            Available Rooms
          </Button>
        </Stack>

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text={`My Activity List (${_activities.length})`} />
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 0, mb: 1 }}>
              <Table aria-label="my activities" size="small">
                <TableHead>
                  <StripedTableRow>
                    <StripedTableCell>Activity Name</StripedTableCell>
                    <StripedTableCell align="center" width={100}>
                      Actions
                    </StripedTableCell>
                  </StripedTableRow>
                </TableHead>
                <TableBody>
                  {_activities.length === 0 && (
                    <StripedTableRow>
                      <StripedTableCell colSpan={10}>
                        -- NO RESULTS RETURNED --
                      </StripedTableCell>
                    </StripedTableRow>
                  )}
                  {_activities.map((activity) => (
                    <StripedTableRow key={activity.eid}>
                      <StripedTableCell>
                        {activity.name} (max: {activity.maxAttendance})
                        {activity.restrictedEnrollment === "Y" &&
                          activity.studentCount + activity.userListCount ===
                            0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              Students cannot see this activity until you add
                              students{" "}
                              <Link
                                onClick={() => handleClickEdit(activity.eid)}
                              >
                                HERE
                              </Link>
                              .
                            </Alert>
                          )}
                      </StripedTableCell>
                      <StripedTableCell align="center">
                        <Menu menuButton={<Button>Actions</Button>} transition>
                          {((_schoolStatus &&
                            _schoolStatus.teacherEditEnabled === "Y") ||
                            getUserPriority(_jwt) === 800) && (
                            <>
                              <MenuItem
                                value={activity.eid}
                                onClick={() => handleClickCalendar()}
                              >
                                Add to Calendar
                              </MenuItem>
                            </>
                          )}
                          <MenuItem
                            value={activity.eid}
                            onClick={() => handleClickEdit(activity.eid)}
                          >
                            Edit
                          </MenuItem>
                          {((_schoolStatus &&
                            _schoolStatus.teacherEditEnabled === "Y") ||
                            getUserPriority(_jwt) === 800) && (
                            <>
                              <MenuItem
                                value={activity.eid}
                                onClick={() => handleClickDelete(activity.eid)}
                              >
                                Delete
                              </MenuItem>
                              <MenuItem
                                value={activity.eid}
                                onClick={() =>
                                  handleClickChangeOwner(activity.eid)
                                }
                              >
                                Change Owner
                              </MenuItem>
                              <MenuItem
                                value={activity.eid}
                                onClick={() => handleClickClone(activity)}
                              >
                                Clone
                              </MenuItem>
                            </>
                          )}
                          <MenuItem
                            value={activity.eid}
                            onClick={() => handleClickWhoCanView(activity.eid)}
                          >
                            Who Can View
                          </MenuItem>
                          <MenuItem
                            value={activity.eid}
                            onClick={() =>
                              handleClickCreateStudentList(activity)
                            }
                          >
                            Create Student List
                          </MenuItem>
                          <MenuItem
                            value={activity.eid}
                            onClick={() =>
                              handleClickPreviousAttendance(activity)
                            }
                          >
                            Previous Attendance
                          </MenuItem>
                          <MenuItem
                            value={activity.eid}
                            onClick={() => handleClickRqr(activity)}
                          >
                            Reusable QR Code
                          </MenuItem>
                        </Menu>
                      </StripedTableCell>
                    </StripedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <SelectTeacherDialog
          open={_dialogOpenOwner}
          onClose={handleCloseDialogOwner}
          onOK={handleOKOwner}
          teachers={_allTeachers}
        />

        <Dialog
          onClose={handleCloseClone}
          open={_dialogOpenClone}
          PaperProps={{
            sx: {
              maxWidth: 500,
              maxHeight: 500,
            },
          }}
        >
          <DialogTitle>Clone an Existing Activity</DialogTitle>

          <DialogContent sx={{ ml: 0, pl: 2, pr: 2 }}>
            <InputLabel>New Activity Name *</InputLabel>
            <TextField
              margin="dense"
              type="text"
              fullWidth
              size="small"
              onChange={handleChangeCloneName}
              placeholder="Required Field"
              value={_cloneName}
            />

            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event) =>
                    handleChangeCloneRestrictedEnrollment(event)
                  }
                  checked={_cloneIncludeRestrictedEnrollment}
                />
              }
              label={"Include 'Who Can View' Students"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event) => handleChangeCloneActivityDates(event)}
                  checked={_cloneActivityDates}
                />
              }
              label={"Include Activity Dates"}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseClone}>Cancel</Button>
            <Button variant="contained" onClick={handleCloneOK}>
              Clone Activity
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ManageActivities;
